import React, { useRef, useMemo } from 'react'
import PageConfig from '@/component/layout/PageConfig.tsx'
import useQueryGetMe from '@/api/auth/getMe.ts'
import SolidButton from '@/component/button/SolidButton.tsx'
import BorderButton from '@/component/button/BorderButton.tsx'
import { useNavigate, Link } from 'react-router-dom'
import ChangePasswordModal from '@/pages/account/ChangePasswordModal.tsx'
import EditUserModal from '@/pages/account/EditUserModal.tsx'
import AddUserModal from '@/pages/account/AddUserModal.tsx'
import AdminThumbnail from '@/component/image/AdminThumbnail.tsx'
import { useTranslation } from 'react-i18next'
import useQueryGetUsers, { IUser } from '@/api/user/getUsers.ts'
import { apiDeleteUser } from '@/api/user/deleteUser.ts'
import { toast } from 'react-toastify'
import { useQueryClient } from '@tanstack/react-query'
import { QueryCacheKeys } from '@/api/queryKeys.ts'

type IProps = {}

export type IEditAccount = {
    name?: string
    isNameEditing: boolean,
    contact: string,
    isContactEditing: boolean,
    representerName: string,
    isRepresenterNameEditing: boolean,
    representerContact: string,
    isRepresenterContactEditing: boolean,
    representerEmail: string,
    isRepresenterEmailEditing: boolean,
    file?: File
}

const calculateAge = (birthDate: string): number => {
    const today = new Date()
    const birth = new Date(birthDate)
    let age = today.getFullYear() - birth.getFullYear()
    const monthDiff = today.getMonth() - birth.getMonth()
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
        age--
    }
    return age
}

const formatBirthDate = (birthDate: string): string => {
    const date = new Date(birthDate)
    if (isNaN(date.getTime())) return birthDate
    const y = date.getFullYear()
    const m = String(date.getMonth() + 1).padStart(2, '0')
    const d = String(date.getDate()).padStart(2, '0')
    return `${y}-${m}-${d}`
}

const Account: React.FC<IProps> = ({}) => {
    const { data: center } = useQueryGetMe()
    const { data: users, isLoading: isUsersLoading } = useQueryGetUsers({})
    const navigate = useNavigate()
    const changePasswordModalRef = useRef<any>()
    const editUserModalRef = useRef<any>()
    const addUserModalRef = useRef<any>()
    const queryClient = useQueryClient()
    const { t } = useTranslation()

    const userList = useMemo(() => {
        if (!users) return []
        if (Array.isArray(users)) return users
        return []
    }, [users])

    const onClickEdit = (user: IUser) => {
        editUserModalRef.current?.show(user)
    }

    const onClickDelete = async (user: IUser) => {
        const confirmed = window.confirm(
            t('account.protectee.delete.confirm', { name: user.name })
        )
        if (!confirmed) return

        try {
            const { code } = await apiDeleteUser(user.id)
            if (code === 200) {
                toast.success(t('account.protectee.delete.success'))
                await queryClient.invalidateQueries({ queryKey: QueryCacheKeys.user.getUsers() })
            } else {
                toast.error(t('account.protectee.delete.error'))
            }
        } catch {
            toast.error(t('account.protectee.delete.error'))
        }
    }

    return <>
        <PageConfig showSideNav={false} showTopNav={true}>
            <div className={'section_1200 mt-[20px]'}>
                {/* 프로필 영역 */}
                <div className={'min-w-[170px] max-w-[300px] mx-auto'}>
                    <h1 className={'text-heading1 text-center mt-[40px]'}>{t('account.title')}</h1>
                    <div className={'grid mt-[30px]'}>
                        <div className={'flex justify-center'}>
                            <AdminThumbnail className={''} src={center?.profileUrl}
                                            imageClassName={'w-[100px] h-[100px]'} />
                        </div>
                        <div className={'grid mt-[24px]'}>
                            <BorderButton size={'small'} text={t('account.changePassword')} className={'w-full'}
                                          onClick={() => changePasswordModalRef.current?.show()} />
                            <p className={'mt-[10px] text-[14px] text-center'}>{t('account.forgotPassword.desc')}</p>
                            <p className={'text-[14px] text-center'}>{t('account.forgotPassword.desc.phone')}</p>
                        </div>
                    </div>
                </div>

                {/* 회원 리스트 영역 */}
                <div className={'mt-[48px] mb-[40px] max-w-[1200px] mx-auto px-[16px]'}>
                    <div className={'flex items-center justify-between mb-[16px]'}>
                        <h2 className={'text-[18px] font-bold'}>
                            {t('account.protectee.title')}
                            {userList.length > 0 &&
                                <span className={'text-[14px] font-normal text-gray-500 ml-[8px]'}>
                                    ({userList.length}{t('account.protectee.count')})
                                </span>
                            }
                        </h2>
                        <SolidButton
                            size={'small'}
                            text={t('account.protectee.add.button')}
                            disabled={true}
                        />
                    </div>

                    <div className={'border border-gray-200 rounded-lg overflow-x-auto'}>
                        <table className={'w-full min-w-[1100px]'}>
                            <thead>
                                <tr className={'bg-gray-50 border-b border-gray-200'}>
                                    <th className={'px-[12px] py-[10px] text-[13px] font-semibold text-gray-600 text-left whitespace-nowrap'}>{t('account.protectee.table.name')}</th>
                                    <th className={'px-[12px] py-[10px] text-[13px] font-semibold text-gray-600 text-center whitespace-nowrap'}>{t('users.detail.gender')}</th>
                                    <th className={'px-[12px] py-[10px] text-[13px] font-semibold text-gray-600 text-center whitespace-nowrap'}>{t('users.detail.birthDate')}</th>
                                    <th className={'px-[12px] py-[10px] text-[13px] font-semibold text-gray-600 text-center whitespace-nowrap'}>{t('account.protectee.table.age')}</th>
                                    <th className={'px-[12px] py-[10px] text-[13px] font-semibold text-gray-600 text-left whitespace-nowrap'}>{t('account.protectee.table.contact')}</th>
                                    <th className={'px-[12px] py-[10px] text-[13px] font-semibold text-gray-600 text-left whitespace-nowrap'}>{t('users.detail.address')}</th>
                                    <th className={'px-[12px] py-[10px] text-[13px] font-semibold text-gray-600 text-left whitespace-nowrap'}>{t('account.protectee.table.deviceSerial')}</th>
                                    <th className={'px-[12px] py-[10px] text-[13px] font-semibold text-gray-600 text-left whitespace-nowrap'}>{t('users.detail.contact.guardian')}</th>
                                    <th className={'px-[12px] py-[10px] text-[13px] font-semibold text-gray-600 text-left whitespace-nowrap'}>{t('users.detail.memo.title')}</th>
                                    <th className={'px-[12px] py-[10px] text-[13px] font-semibold text-gray-600 text-center whitespace-nowrap'}>{t('account.protectee.table.actions')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {isUsersLoading && (
                                    <tr>
                                        <td colSpan={10} className={'px-[16px] py-[40px] text-center text-[14px] text-gray-400'}>
                                            {t('common.loading')}
                                        </td>
                                    </tr>
                                )}

                                {!isUsersLoading && userList.length === 0 && (
                                    <tr>
                                        <td colSpan={10} className={'px-[16px] py-[40px] text-center text-[14px] text-gray-400'}>
                                            {t('account.protectee.empty')}
                                        </td>
                                    </tr>
                                )}

                                {!isUsersLoading && userList.map((user: IUser) => (
                                    <tr
                                        key={user.id}
                                        className={'border-b border-gray-100 last:border-b-0 hover:bg-gray-50 transition-colors'}
                                    >
                                        <td className={'px-[12px] py-[10px] text-[14px] text-gray-900 whitespace-nowrap'}>
                                            {user.name}
                                        </td>
                                        <td className={'px-[12px] py-[10px] text-[14px] text-gray-700 text-center whitespace-nowrap'}>
                                            {user.gender === 0 ? t('common.gender.male') : user.gender === 1 ? t('common.gender.female') : '-'}
                                        </td>
                                        <td className={'px-[12px] py-[10px] text-[14px] text-gray-700 text-center whitespace-nowrap'}>
                                            {user.birthDate ? formatBirthDate(user.birthDate) : '-'}
                                        </td>
                                        <td className={'px-[12px] py-[10px] text-[14px] text-gray-700 text-center whitespace-nowrap'}>
                                            {user.birthDate
                                                ? t('account.protectee.table.ageValue', { age: calculateAge(user.birthDate) })
                                                : '-'
                                            }
                                        </td>
                                        <td className={'px-[12px] py-[10px] text-[14px] text-gray-700 whitespace-nowrap'}>
                                            {user.contact || '-'}
                                        </td>
                                        <td className={'px-[12px] py-[10px] text-[14px] text-gray-500 max-w-[180px] truncate'} title={user.address}>
                                            {user.address || '-'}
                                        </td>
                                        <td className={'px-[12px] py-[10px] text-[14px] text-gray-500 font-mono whitespace-nowrap'}>
                                            {user.device?.device_serial || '-'}
                                        </td>
                                        <td className={'px-[12px] py-[10px] text-[14px] text-gray-700 whitespace-nowrap'}>
                                            {user.primaryRelation
                                                ? `${user.primaryRelation} ${user.primaryContact || ''}`
                                                : '-'
                                            }
                                        </td>
                                        <td className={'px-[12px] py-[10px] text-[14px] text-gray-500 max-w-[150px] truncate'} title={user.inspectorMemo}>
                                            {user.inspectorMemo || '-'}
                                        </td>
                                        <td className={'px-[12px] py-[10px] whitespace-nowrap'}>
                                            <div className={'flex items-center justify-center gap-[6px]'}>
                                                <button
                                                    className={'text-[12px] px-[10px] py-[5px] bg-main_b text-white rounded hover:opacity-80 transition-opacity'}
                                                    onClick={() => onClickEdit(user)}
                                                >
                                                    {t('account.protectee.table.edit')}
                                                </button>
                                                <button
                                                    className={'text-[12px] px-[10px] py-[5px] bg-white text-red-500 border border-red-300 rounded hover:bg-red-50 transition-colors'}
                                                    onClick={() => onClickDelete(user)}
                                                >
                                                    {t('account.protectee.table.delete')}
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* 약관 및 정책 */}
                <div className={'mt-[48px] mb-[40px] max-w-[1200px] mx-auto px-[16px]'}>
                    <h2 className={'text-[18px] font-bold mb-[16px]'}>약관 및 정책</h2>
                    <div className={'border border-gray-200 rounded-lg divide-y divide-gray-200'}>
                        <Link to={'/terms'} className={'flex items-center justify-between px-[16px] py-[14px] text-[14px] text-gray4 hover:bg-gray-50 transition-colors'}>
                            <span>이용약관</span>
                            <span className={'text-gray3'}>&gt;</span>
                        </Link>
                        <Link to={'/privacy'} className={'flex items-center justify-between px-[16px] py-[14px] text-[14px] text-gray4 hover:bg-gray-50 transition-colors'}>
                            <span>개인정보처리방침</span>
                            <span className={'text-gray3'}>&gt;</span>
                        </Link>
                        <Link to={'/location-terms'} className={'flex items-center justify-between px-[16px] py-[14px] text-[14px] text-gray4 hover:bg-gray-50 transition-colors'}>
                            <span>위치정보 이용약관</span>
                            <span className={'text-gray3'}>&gt;</span>
                        </Link>
                    </div>
                </div>

                {/* 확인 버튼 */}
                <div className={'max-w-[300px] mx-auto mb-[60px]'}>
                    <SolidButton text={t('account.confirm')} className={'w-full'}
                                 onClick={() => navigate(-1)} />
                </div>
            </div>
        </PageConfig>
        <ChangePasswordModal ref={changePasswordModalRef} />
        <EditUserModal ref={editUserModalRef} />
        <AddUserModal ref={addUserModalRef} centerId={center?.id} />
    </>
}

export default Account
